import Link from 'next/link'
import React, { useEffect, useMemo, useState } from 'react'

import { trackEcommerce, META_EVENTS, useMetaTrack } from '@/shared/analytics'
import { Button } from '@/components/Button/Button'
import { Icon } from '@/components/Icon/Icon'
import { StyledBreadcrumbLink } from '@/shared/components/Breadcrumbs/BreadcrumbLink'
import { Breadcrumbs } from '@/shared/components/Breadcrumbs/Breadcrumbs'
import { CustomImage } from '@/shared/components/CustomImage/CustomImage'
import { ImageLightbox } from '@/shared/components/ProductCarousel/ImageLightbox/ImageLightbox'
import { ProductCarousel } from '@/shared/components/ProductCarousel/ProductCarousel'
import { formatUsd } from '@/shared/libs/money'
import { useMarket } from '@/shared/market'
import { pxToRem } from '@/shared/styles/themes'
import { faArrowRight } from '@fortawesome/pro-light-svg-icons/faArrowRight'
import { faBookOpen } from '@fortawesome/pro-light-svg-icons/faBookOpen'
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck'
import { Box, Container, Divider, Grid, styled, Typography, useMediaQuery, useTheme } from '@mui/material'

import type { PremadeStoryDetail } from '@/shared/api/models'
import { selectDefaultBook, useShopCatalog } from '../useShopCatalog'
import { SampleStoryPreview } from './SampleStoryPreview'
import { LiveActivityTicker, STOREFRONT_DATA_MAP, StorefrontTrustSignals } from './TrustSignals'
interface PremadeStoryProductPageProps {
  story: PremadeStoryDetail
}

const getPremadeFeatures = (spelling: { personalised: string }) => [
  `${spelling.personalised.charAt(0).toUpperCase() + spelling.personalised.slice(1)} with your child's name and look`,
  'Choose from multiple character looks',
  'First digital story is free — no subscription',
  'Read instantly on any device, or order a hardcover',
]

export const PremadeStoryProductPage: React.FC<PremadeStoryProductPageProps> = ({
  story,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { market } = useMarket()
  const premadeFeatures = getPremadeFeatures(market.spelling)

  const trackMeta = useMetaTrack()

  // The storybook itself is free to personalise; the hardcover is what's
  // sold, so its catalog price is the value we report for view tracking.
  const { data: catalog, isFetched: isCatalogFetched } = useShopCatalog()
  const defaultBook = selectDefaultBook(catalog)

  useEffect(() => {
    if (!isCatalogFetched) return

    const price = defaultBook?.price ?? '0'

    trackEcommerce('view_item', [{
      item_id: story.slug,
      item_name: story.title,
      price: parseFloat(price),
      item_category: 'Personalised Storybook',
    }], parseFloat(price), 'USD')

    trackMeta({
      event_name: META_EVENTS.VIEW_CONTENT,
      item_id: [story.slug],
      item_name: story.title,
      price,
      currency: 'USD',
      item_category: 'Personalised Storybook',
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCatalogFetched, story.slug])

  const [previewExpanded, setPreviewExpanded] = useState(false)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  const images = useMemo(() => {
    if (story.gallery_images?.length) {
      return story.gallery_images.map((img) => ({
        url: img.url,
        alt: img.alt || story.title,
      }))
    }

    if (story.thumbnail_url) {
      return [{ url: story.thumbnail_url, alt: story.title }]
    }

    return []
  }, [story.gallery_images, story.title, story.thumbnail_url])

  const lightboxImages = useMemo(
    () => images.map(({ url, alt }) => ({ src: url, loading: 'lazy' as const, alt })),
    [images],
  )

  const { title } = story

  const onImageClick = (index?: number) => {
    setLightboxIndex(index || 0)
    setTimeout(() => {
      setLightboxOpen(true)
    }, 10)
  }

  const onLightBoxClose = () => {
    setLightboxOpen(false)
  }

  return (
    <Container maxWidth="lg">
      <StyledPageWrapper>
        <Breadcrumbs sx={{ mb: 3 }}>
          <StyledBreadcrumbLink href="/shop">Shop</StyledBreadcrumbLink>
          <Typography variant="body2" color="text.primary" fontWeight={500}>
            {title}
          </Typography>
        </Breadcrumbs>

        <Grid container spacing={4}>
          {/* Left column — Images */}
          <Grid item xs={12} md={7}>
            <StyledCarouselWrapper>
              <ProductCarousel wrapper>
                {!isMobile && images.length > 1 && (
                  <ProductCarousel imagelist direction="column" spacing={pxToRem(10)}>
                    {images.map((img, index) => (
                      <ProductCarousel thumb key={index} index={index}>
                        <CustomImage src={img.url} alt={img.alt} objectPosition="50% 50%" />
                      </ProductCarousel>
                    ))}
                  </ProductCarousel>
                )}
                <ProductCarousel slider>
                  {images.map((img, index) => (
                    <ProductCarousel
                      slide
                      onImageClick={onImageClick}
                      src={img.url}
                      alt={img.alt}
                      fill
                      style={{ objectFit: 'contain' }}
                      key={index}
                      index={index}
                    />
                  ))}
                </ProductCarousel>
                {isMobile && images.length > 1 && (
                  <ProductCarousel imagelist direction="row">
                    {images.map((img, index) => (
                      <ProductCarousel thumb key={index} index={index}>
                        <CustomImage src={img.url} alt={img.alt} objectPosition="50% 50%" />
                      </ProductCarousel>
                    ))}
                  </ProductCarousel>
                )}
                <ImageLightbox
                  open={lightboxOpen}
                  setCurrentIndex={setLightboxIndex}
                  currentImageIndex={lightboxIndex}
                  onClose={onLightBoxClose}
                  images={lightboxImages}
                />
              </ProductCarousel>
            </StyledCarouselWrapper>
          </Grid>

          {/* Right column — Details */}
          <Grid item xs={12} md={5}>
            <StyledDetailsColumn>
              <Typography variant="h4" component="h1" fontWeight="bold">
                {title}
              </Typography>

              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                {story.description}
              </Typography>

              <LiveActivityTicker
                storySlug={story.slug}
                data={STOREFRONT_DATA_MAP[story.slug]?.live_activity}
              />

              <StyledOfferCallout href={`/shop/${story.slug}/personalize`}>
                <StyledOfferRow>
                  <StyledOfferAmount>
                    <StyledOfferPrice>Free</StyledOfferPrice>
                    <StyledOfferUnit>digital personalisation</StyledOfferUnit>
                  </StyledOfferAmount>
                  <StyledOfferHint>Make this story your own — your child as the hero on every page.</StyledOfferHint>
                </StyledOfferRow>
                {defaultBook && (
                  <>
                    <StyledOfferDivider />
                    <StyledOfferRow>
                      <StyledOfferAmount>
                        <StyledOfferPrice>{formatUsd(defaultBook.price)}</StyledOfferPrice>
                        <StyledOfferUnit>premium {defaultBook.cover_type || 'hardcover'}</StyledOfferUnit>
                      </StyledOfferAmount>
                      <StyledOfferHint>Add a printed hardcover at checkout. Linen-bound, shipped to your door.</StyledOfferHint>
                    </StyledOfferRow>
                  </>
                )}
              </StyledOfferCallout>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                What&apos;s included
              </Typography>
              <StyledFeatureList>
                {premadeFeatures.map((feature) => (
                  <StyledFeatureItem key={feature}>
                    <Icon icon={faCheck} />
                    <Typography variant="body1">{feature}</Typography>
                  </StyledFeatureItem>
                ))}
              </StyledFeatureList>

              <StyledButtonGroup>
                <Link href={`/shop/${story.slug}/personalize`} passHref legacyBehavior>
                  <Button
                    primary
                    size="large"
                    fullWidth
                    component="a"
                  >
                    Personalise Book
                    <Icon icon={faArrowRight} style={{ marginLeft: 8 }} />
                  </Button>
                </Link>
                {story.sample_story_share_slugs?.length > 0 && (
                  <Button
                    size="large"
                    fullWidth
                    onClick={() => setPreviewExpanded((v) => !v)}
                  >
                    <Icon icon={faBookOpen} style={{ marginRight: 8 }} />
                    Preview Sample Story
                  </Button>
                )}
              </StyledButtonGroup>

            </StyledDetailsColumn>
          </Grid>
        </Grid>
      </StyledPageWrapper>
      {story.sample_story_share_slugs?.length > 0 && (
        <SampleStoryPreview
          slugs={story.sample_story_share_slugs}
          expanded={previewExpanded}
        />
      )}
      <StorefrontTrustSignals storySlug={story.slug} />
    </Container>
  )
}

const StyledCarouselWrapper = styled(Box)(({ theme }) => ({
  '& > .MuiGrid-root': {
    [theme.breakpoints.up('md')]: {
      overflowX: 'hidden',
      flexWrap: 'nowrap',
    },
  },
  '& > .MuiGrid-root > div:has(.carousel)': {
    [theme.breakpoints.up('sm')]: {
      width: '100%',
      maxWidth: 'none',
      flex: '1 1 0',
      minWidth: 0,
    },
  },
}))

const StyledPageWrapper = styled(Box)`
  padding: ${pxToRem(32)} 0 ${pxToRem(64)};
`

const StyledDetailsColumn = styled(Box)`
  position: sticky;
  top: ${pxToRem(24)};
`

const StyledOfferCallout = styled(Link)`
  display: block;
  margin-top: ${pxToRem(16)};
  padding: ${pxToRem(18)} ${pxToRem(20)};
  border-radius: ${pxToRem(14)};
  background-color: ${(props) => props.theme.palette.primary.main};
  color: #FFFFFF;
  text-decoration: none;
  transition: filter 0.2s ease;

  &:hover {
    filter: brightness(1.08);
  }
`

const StyledOfferRow = styled('div')`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(4)};
`

const StyledOfferAmount = styled('div')`
  display: flex;
  align-items: baseline;
  gap: ${pxToRem(10)};
  flex-wrap: wrap;
`

const StyledOfferPrice = styled('span')`
  font-size: ${pxToRem(28)};
  font-weight: 700;
  line-height: 1;
`

const StyledOfferUnit = styled('span')`
  font-size: ${pxToRem(13)};
  font-weight: 600;
  letter-spacing: ${pxToRem(0.4)};
  text-transform: uppercase;
  opacity: 0.85;
`

const StyledOfferHint = styled('div')`
  font-size: ${pxToRem(13)};
  opacity: 0.9;
  line-height: 1.45;
`

const StyledOfferDivider = styled('div')`
  margin: ${pxToRem(14)} 0;
  height: 1px;
  background-color: rgba(255, 255, 255, 0.18);
`

const StyledFeatureList = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(10)};
  margin-top: ${pxToRem(8)};
`

const StyledFeatureItem = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${pxToRem(10)};
  color: ${(props) => props.theme.palette.success.main};

  & > p {
    color: ${(props) => props.theme.palette.text.primary};
  }
`

const StyledButtonGroup = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(12)};
  margin-top: ${pxToRem(28)};
`
